/**
 * Reports, Phases (migration 0091; the #55 plan's §C.7.4): §C.3's sections by
 * phase, in the Projects report's table. The project filter reads a project
 * and every project under it, so "Lot 12 by cost code" and "the whole
 * subdivision by cost code" are each one choice; with none it reads every
 * line, so "all design work, every client".
 */

import { useEffect, useState } from 'react';
import { ApiError, api, type PhaseReport, type Project, type ProjectReport } from '../api.js';
import { ProjectReportTable } from './ProjectReports.js';

/** The phase report in the Projects table's shape: each phase's id where a project's would be. */
function asTable(report: PhaseReport): ProjectReport {
  const { project: _narrowed, columns, ...rest } = report;
  return { ...rest, columns: columns.map((column) => ({ projectId: column.phaseId, name: column.name, function: null, archived: column.archived })) };
}

export function PhaseReportView(props: { from: string; to: string; word: 'Program' | 'Project'; initialProjectId?: string | null }) {
  const [projects, setProjects] = useState<Project[]>([]);
  // `?project=` in the address opens the report narrowed, so a link can name the project.
  const [projectId, setProjectId] = useState(props.initialProjectId ?? '');
  const [report, setReport] = useState<PhaseReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .listProjects()
      .then((result) => live && setProjects(result.projects))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    let live = true;
    setError(null);
    api
      .phaseReport(props.from, props.to, projectId === '' ? undefined : projectId)
      .then((result) => live && setReport(result))
      .catch((caught: unknown) => live && setError(caught instanceof ApiError ? caught.message : 'Could not build the report.'));
    return () => {
      live = false;
    };
  }, [props.from, props.to, projectId]);

  return (
    <>
      {projects.length > 0 && (
        <div className="row report-period">
          <label>
            {props.word}{' '}
            <select aria-label={`${props.word} to read`} value={projectId} onChange={(event) => setProjectId(event.target.value)}>
              <option value="">Every {props.word}</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.path ?? project.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {report !== null && <ProjectReportTable report={asTable(report)} word="Phase" narrowedTo={report.project?.name ?? null} />}
    </>
  );
}
